import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { TrialOffer } from '../plan-v2/trial-offer';

interface Props {
  offer: TrialOffer;
  onActivate: () => void;
}

/**
 * The offer to end the free trial and pay now (RN-10).
 *
 * Says the date the trial ends, never a countdown (RN-17), and states the
 * charge beside the button so nobody pays by accident (AR-07).
 *
 * @param props - The offer and what the button does.
 * @returns The strip.
 */
export function TrialUpgradeStrip({ offer, onActivate }: Props) {
  const { t, i18n } = useTranslation('features/subscription');
  const plan = offer.planName;

  return (
    <section
      data-testid="trial-upgrade-strip"
      aria-label={t('trial.strip.eyebrow', { date: formatLocalizedDate(offer.trialEnd, i18n.language) })}
      className="flex items-center justify-between gap-(--space-4) flex-wrap"
      style={{
        padding: 'var(--space-4) var(--space-5)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-raised)',
      }}
    >
      <div className="flex flex-col gap-(--space-1-5) min-w-0" style={{ flex: '1 1 26rem' }}>
        <Text component="span" style={{
          fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)', color: 'var(--seed-base)',
        }}>
          {t('trial.strip.eyebrow', { date: formatLocalizedDate(offer.trialEnd, i18n.language) })}
        </Text>
        <Text component="strong" style={{
          fontSize: 'var(--text-app-section)', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
        }}>
          {t('trial.strip.title', { plan })}
        </Text>
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
          {t('trial.strip.body', { interactions: offer.interactions, searches: offer.searches })}
        </Text>
      </div>
      <div className="flex flex-col items-center gap-(--space-1-5)">
        <Button data-testid="trial-activate-button" onClick={onActivate}>
          {t('trial.strip.action', { plan })}
        </Button>
        <Text component="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)' }}>
          {t('trial.strip.note', { price: formatCents(offer.cents, { locale: i18n.language }) })}
        </Text>
      </div>
    </section>
  );
}
